import dagre from 'dagre';
import { ParsedGraph } from '../types/ontology';

export interface LayoutNode {
    id: string;
    uri: string;
    label: string;
    type: 'class' | 'property' | 'individual';
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface LayoutEdge {
    id: string;
    source: string;
    target: string;
    type: 'subClassOf' | 'domain' | 'range' | 'property';
    label?: string;
    path: { x: number; y: number }[];
}

export interface GraphLayout {
    nodes: LayoutNode[];
    edges: LayoutEdge[];
}

const NODE_WIDTH = 160;
const NODE_HEIGHT = 60;
const GRID_SIZE = 20;

export function calculateLayout(graph: ParsedGraph): GraphLayout {
    const nodes: LayoutNode[] = [];
    const edges: LayoutEdge[] = [];

    // Create Dagre graph for layout calculation
    const g = new dagre.graphlib.Graph();
    g.setGraph({
        rankdir: 'TB',  // Top to bottom
        nodesep: 80,
        ranksep: 100,
        marginx: 50,
        marginy: 50,
    });
    g.setDefaultEdgeLabel(() => ({}));

    // Add class nodes
    graph.classes.forEach(cls => {
        const id = getLocalName(cls.uri);
        g.setNode(id, { label: cls.label, width: NODE_WIDTH, height: NODE_HEIGHT });
        nodes.push({
            id,
            uri: cls.uri,
            label: cls.label,
            type: 'class',
            x: 0,
            y: 0,
            width: NODE_WIDTH,
            height: NODE_HEIGHT,
        });
    });

    // Add subclass edges
    graph.classes.forEach(cls => {
        if (cls.subClassOf) {
            cls.subClassOf.forEach(superClass => {
                const sourceId = getLocalName(cls.uri);
                const targetId = getLocalName(superClass);
                if (g.hasNode(sourceId) && g.hasNode(targetId)) {
                    g.setEdge(sourceId, targetId);
                    edges.push({
                        id: `${sourceId}-subClassOf-${targetId}`,
                        source: sourceId,
                        target: targetId,
                        type: 'subClassOf',
                        label: 'subClassOf',
                        path: [],
                    });
                }
            });
        }
    });

    // Add property edges (domain -> range)
    graph.objectProperties.forEach(prop => {
        if (prop.domain && prop.range) {
            prop.domain.forEach(domain => {
                prop.range!.forEach(range => {
                    const sourceId = getLocalName(domain);
                    const targetId = getLocalName(range);
                    if (g.hasNode(sourceId) && g.hasNode(targetId)) {
                        g.setEdge(sourceId, targetId);
                        edges.push({
                            id: `${sourceId}-${prop.label}-${targetId}`,
                            source: sourceId,
                            target: targetId,
                            type: 'property',
                            label: prop.label,
                            path: [],
                        });
                    }
                });
            });
        }
    });

    // Calculate layout
    dagre.layout(g);

    // Apply positions to nodes
    g.nodes().forEach(nodeId => {
        const node = g.node(nodeId);
        const layoutNode = nodes.find(n => n.id === nodeId);
        if (layoutNode && node) {
            // Snap to grid
            layoutNode.x = snapToGrid(node.x - NODE_WIDTH / 2);
            layoutNode.y = snapToGrid(node.y - NODE_HEIGHT / 2);
        }
    });

    // Calculate PCB-style edge paths
    edges.forEach(edge => {
        const sourceNode = nodes.find(n => n.id === edge.source);
        const targetNode = nodes.find(n => n.id === edge.target);

        if (sourceNode && targetNode) {
            edge.path = calculatePCBPath(sourceNode, targetNode, nodes);
        }
    });

    return { nodes, edges };
}

function getLocalName(uri: string): string {
    const hashIndex = uri.lastIndexOf('#');
    const slashIndex = uri.lastIndexOf('/');
    const index = Math.max(hashIndex, slashIndex);
    return index >= 0 ? uri.substring(index + 1) : uri;
}

function snapToGrid(value: number): number {
    return Math.round(value / GRID_SIZE) * GRID_SIZE;
}

/**
 * Calculate PCB-style orthogonal path between two nodes
 * Uses A* pathfinding on a grid, avoiding other nodes
 */
function calculatePCBPath(
    source: LayoutNode,
    target: LayoutNode,
    _allNodes: LayoutNode[]
): { x: number; y: number }[] {
    // Source bottom center
    const startX = source.x + source.width / 2;
    const startY = source.y + source.height;

    // Target top center
    const endX = target.x + target.width / 2;
    const endY = target.y;

    // Simple orthogonal routing (can be improved with A* later)
    const path: { x: number; y: number }[] = [];

    path.push({ x: startX, y: startY });

    // Calculate intermediate points for PCB-style routing
    const midY = (startY + endY) / 2;

    if (Math.abs(startX - endX) < 5) {
        // Nodes are vertically aligned - straight line
        path.push({ x: endX, y: endY });
    } else if (startY < endY) {
        // Target is below source
        // Go down, then horizontal, then down
        path.push({ x: startX, y: midY });
        path.push({ x: endX, y: midY });
        path.push({ x: endX, y: endY });
    } else {
        // Target is above or at same level - route around
        const offsetY = Math.max(startY, endY) + 40;
        const offsetX = startX < endX ? source.x - 30 : source.x + source.width + 30;

        path.push({ x: startX, y: offsetY });
        path.push({ x: offsetX, y: offsetY });
        path.push({ x: offsetX, y: endY - 40 });
        path.push({ x: endX, y: endY - 40 });
        path.push({ x: endX, y: endY });
    }

    return path;
}
